import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LoaderService } from './loader.service';
import { ApiEndpoints } from '@shared/common/apiPoint';

/**
 * URLs that should use the GLOBAL loader (full-screen overlay)
 * By default, all other URLs will use LOCAL loader (component-level)
 *
 * Add URLs here only for critical operations that should block the entire UI:
 * - Authentication/Login
 * - Initial data loading
 * - Critical configuration updates
 */
export const globalLoaderUrls = [
  '/v1/provider/authentication/login',
  ApiEndpoints.Identity.Authenticate,
  ApiEndpoints.Identity.DispatcherLogin,
  ApiEndpoints.Identity.CustomerLogin,
  ApiEndpoints.Identity.DriverLogin,
  ApiEndpoints.Identity.Logout,
];

export const localLoaderUrls = [
  // BICard endpoints - primary data fetching (non-action endpoints)
  ApiEndpoints.BICard.get_overtime_header,
  ApiEndpoints.BICard.get_deadhead_header,
  ApiEndpoints.BICard.get_paid_routed_time_header,
  ApiEndpoints.BICard.get_time_monitoring,
  ApiEndpoints.BICard.get_time_trending,
  ApiEndpoints.BICard.get_rout_schdule_actul_paid,
  ApiEndpoints.BICard.get_schedule_vs_actual_heatmap,
  ApiEndpoints.BICard.get_labor_schedule_vs_actual_bar_chart,
  ApiEndpoints.BICard.byte_curve_metric_status,
  ApiEndpoints.BICard.buffer_times,
  ApiEndpoints.BICard.employee_attendance_analysis,

  // Safety Dashboard endpoints - primary data fetching (non-action endpoints)
  ApiEndpoints.SafetyDashboard.get_risk_per_mile,
  ApiEndpoints.SafetyDashboard.get_collision_per_mile,
  ApiEndpoints.SafetyDashboard.get_injury_data,
  ApiEndpoints.SafetyDashboard.get_risk_per_mile_line_chart,
  ApiEndpoints.SafetyDashboard.get_collision_per_mile_line_chart,
  ApiEndpoints.SafetyDashboard.get_injury_per_hours_line_chart,
  ApiEndpoints.SafetyDashboard.get_risk_distribution,
  ApiEndpoints.SafetyDashboard.get_risk_top_behavior_incidents,
  ApiEndpoints.SafetyDashboard.get_risk_top_drivers,
  ApiEndpoints.SafetyDashboard.get_risk_drivers_coachablescores,
  ApiEndpoints.SafetyDashboard.get_risk_recurring_drivers,
  ApiEndpoints.SafetyDashboard.get_risk_top_coachable_scores,
  ApiEndpoints.SafetyDashboard.get_risk_drivers_month_over_month,
  ApiEndpoints.SafetyDashboard.get_safety_collision_count,
  ApiEndpoints.SafetyDashboard.get_safety_locations_by_collisions,
  ApiEndpoints.SafetyDashboard.get_collision_by_drivers,
  ApiEndpoints.SafetyDashboard.get_top_locations_by_injuries,
  ApiEndpoints.SafetyDashboard.get_safety_injuries_by_employees,
  ApiEndpoints.SafetyDashboard.get_safety_injuries_lost_time,
  ApiEndpoints.SafetyDashboard.get_safety_injuries_count,
  ApiEndpoints.SafetyDashboard.get_collision_top_relevant_factors,
  ApiEndpoints.SafetyDashboard.get_collision_top_counts,
  ApiEndpoints.SafetyDashboard.get_injury_top_relevant_conditions,
];

export const loaderInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const loaderService = inject(LoaderService);

  const shouldUseLocalLoader = localLoaderUrls.some(url => req.url === url);

  if (!shouldUseLocalLoader) {
    loaderService.show();
  } else {
    loaderService.showLocal();
  }

  return next(req).pipe(
    catchError(error => {
      if (!shouldUseLocalLoader) {
        loaderService.hide();
      } else {
        loaderService.hideLocal();
      }
      throw error;
    }),
    finalize(() => {
      if (!shouldUseLocalLoader) {
        loaderService.hide();
      } else {
        loaderService.hideLocal();
      }
    }),
  );
};
